import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';
import { useAuthStore } from '~/store/auth.store';

import { fetchMyPreferences, updateMyPreferences } from '../services/my-preferences.service';
import type { MyPreferences } from '../types/my-preferences.types';

/** The signed-in user's own preferences; `enabled` off skips the request (e.g. customers). */
export function useMyPreferences(options?: { enabled?: boolean }) {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  return useQuery({
    queryKey: queryKeys.myPreferences.detail(userId ?? ''),
    queryFn: fetchMyPreferences,
    enabled: userId !== null && (options?.enabled ?? true),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** Saves a preference; flips the cache first so every cost surface re-renders at once,
 *  rolls back on failure. */
export function useUpdateMyPreferences() {
  const qc = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const key = queryKeys.myPreferences.detail(userId ?? '');
  return useMutation({
    mutationFn: updateMyPreferences,
    onMutate: async (values) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<MyPreferences>(key);
      if (previous) qc.setQueryData<MyPreferences>(key, { ...previous, ...values });
      return { previous };
    },
    onError: (_err, _values, context) => {
      if (context?.previous) qc.setQueryData(key, context.previous);
    },
    onSuccess: (saved) => {
      qc.setQueryData(key, saved);
    },
  });
}
